import { useMemo } from "react";

import { useRightRail } from "@/context/RightRailContext";
import type { KaiMailReviewContext } from "@/services/kaiApi";
import { useKaiChat } from "./KaiChatContext";
import { buildMailReviewUserPrompt } from "./kaiMailReviewPrompt";

export interface KaiMarkdownActions {
  /** Send a guidance practical offer back to Kai as a follow-up message. */
  onSendFollowUp?: (text: string) => void;
  /** Close the Kai rail before navigating to an internal deep link. */
  beforeNavigate: () => void;
  /**
   * Drill into a single email from a mail-review card: seed the active email so
   * Kai opens it with `get_graph_mail_context`, then send a short prompt.
   */
  onReviewEmail?: (ctx: KaiMailReviewContext, text: string) => void;
}

/**
 * Handlers threaded into the Kai markdown renderer so structured blocks can
 * send follow-up prompts (guidance / mail review), drill into a specific email,
 * and dismiss the rail on internal navigation (rate comparison / action
 * proposal deep links).
 */
export function useKaiMarkdownActions(): KaiMarkdownActions {
  const kai = useKaiChat();
  const { closeKai } = useRightRail();

  return useMemo(
    () => ({
      onSendFollowUp: kai?.sendMessage,
      beforeNavigate: closeKai,
      onReviewEmail: kai
        ? (ctx: KaiMailReviewContext, text: string) => {
            kai.setMailReviewFocus(ctx);
            kai.sendMessage(buildMailReviewUserPrompt(ctx, text));
          }
        : undefined,
    }),
    [kai, closeKai],
  );
}
